/**
 * Accounting Standard Selector
 *
 * Modal/section for choosing between K2 and K3 accounting standards.
 * Shows auto-detected recommendation based on company size thresholds
 * and allows user to override.
 *
 * K2: BFNAR 2016:10 - Förenklat regelverk för mindre företag
 * K3: BFNAR 2012:1 - Huvudregelverk med kassaflödesanalys, uppskjuten skatt etc.
 *
 * Thresholds (two consecutive years):
 * - Employees > 50
 * - Balance sheet total > 40 MSEK
 * - Net revenue > 80 MSEK
 */

import { useState, useRef } from "react";
import {
  Scale,
  Building2,
  TrendingUp,
  Users,
  FileText,
  Loader2,
  X,
  Check,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { reportsApi, type AnnualReport, type UserInput } from "@/lib/api";
import { formatNumber } from "@/lib/format";

// Thresholds for K3 requirement (BFNAR 2006:11)
const K3_THRESHOLDS = {
  employees: 50,
  balanceSheetTotal: 40_000_000, // 40 MSEK
  netRevenue: 80_000_000, // 80 MSEK
};

type AccountingStandard = "K2" | "K3";

interface AccountingStandardSelectorProps {
  isOpen: boolean;
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  onConfirmed: (standard: AccountingStandard) => void;
  onClose?: () => void;
  /** If true, user cannot change the standard (locked after step 2) */
  isLocked?: boolean;
  /** Custom save handler for guest mode (bypasses API) */
  onSaveInput?: (
    data: Record<string, unknown>
  ) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
}

interface ThresholdCheck {
  id: string;
  label: string;
  currentValue: number | null;
  threshold: number;
  exceeded: boolean;
  unit: string;
  icon: React.ReactNode;
}

/**
 * Analyze report data to determine K3 recommendation
 */
function analyzeK3Requirement(report: AnnualReport): {
  recommended: AccountingStandard;
  checks: ThresholdCheck[];
  isParentCompany: boolean;
  mustUseK3: boolean;
  reason?: string;
} {
  const sieData = report.parsedSieData as {
    balanceSheet?: {
      assets?: { totalAssets?: number };
    };
    incomeStatement?: {
      revenue?: { netSales?: number };
    };
    keyFigures?: {
      totalAssets?: number;
      netSales?: number;
    };
  } | null;

  const userInput = report.userInput as UserInput | null;

  // Extract values from SIE data
  const balanceSheetTotal =
    sieData?.balanceSheet?.assets?.totalAssets ??
    sieData?.keyFigures?.totalAssets ??
    0;

  const netRevenue =
    sieData?.incomeStatement?.revenue?.netSales ??
    sieData?.keyFigures?.netSales ??
    0;

  // Employees from user input (may not be filled yet)
  const employees = userInput?.averageEmployees ?? null;

  // Check if company is a parent company (has subsidiaries)
  const isParentCompany = userInput?.consolidation?.isParentCompany ?? false;

  // Build threshold checks
  const checks: ThresholdCheck[] = [
    {
      id: "employees",
      label: "Medelantal anställda",
      currentValue: employees,
      threshold: K3_THRESHOLDS.employees,
      exceeded: employees !== null && employees > K3_THRESHOLDS.employees,
      unit: "personer",
      icon: <Users size={18} />,
    },
    {
      id: "balanceSheet",
      label: "Balansomslutning",
      currentValue: balanceSheetTotal,
      threshold: K3_THRESHOLDS.balanceSheetTotal,
      exceeded: balanceSheetTotal > K3_THRESHOLDS.balanceSheetTotal,
      unit: "kr",
      icon: <Scale size={18} />,
    },
    {
      id: "netRevenue",
      label: "Nettoomsättning",
      currentValue: netRevenue,
      threshold: K3_THRESHOLDS.netRevenue,
      exceeded: netRevenue > K3_THRESHOLDS.netRevenue,
      unit: "kr",
      icon: <TrendingUp size={18} />,
    },
  ];

  // Count how many thresholds are exceeded
  const exceededCount = checks.filter((c) => c.exceeded).length;

  // K3 is required if:
  // 1. More than one threshold exceeded for two consecutive years
  // 2. Company is a parent that must prepare consolidated accounts
  // For simplicity, we recommend K3 if any threshold is exceeded
  const mustUseK3 = isParentCompany;
  const recommended: AccountingStandard =
    mustUseK3 || exceededCount >= 2 ? "K3" : "K2";

  let reason: string | undefined;
  if (mustUseK3) {
    reason = "Moderbolag som upprättar koncernredovisning måste använda K3.";
  } else if (exceededCount >= 2) {
    reason = `Företaget överskrider ${exceededCount} av 3 gränsvärden och klassificeras som större företag.`;
  }

  return { recommended, checks, isParentCompany, mustUseK3, reason };
}

export function AccountingStandardSelector({
  isOpen,
  report,
  onUpdate,
  onConfirmed,
  onClose,
  isLocked = false,
  onSaveInput,
}: AccountingStandardSelectorProps) {
  const analysis = analyzeK3Requirement(report);
  const currentStandard =
    (report.accountingStandard as AccountingStandard) ?? "K2";

  const [selectedStandard, setSelectedStandard] =
    useState<AccountingStandard>(currentStandard);
  const [showDetails, setShowDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleConfirm = async () => {
    setIsSaving(true);

    try {
      // Use custom save handler for guest mode, otherwise use API
      if (onSaveInput) {
        // For guest mode, just confirm (standard is handled in localStorage)
        toast.success(
          `Redovisningsstandard ${selectedStandard} vald`
        );
        onConfirmed(selectedStandard);
      } else {
        const response = await reportsApi.updateStandard(
          report.id,
          selectedStandard
        );
        if (response.success && response.data) {
          onUpdate(response.data.report);
          toast.success(
            `Redovisningsstandard ${selectedStandard} vald`
          );
          onConfirmed(selectedStandard);
        } else {
          toast.error(
            response.error?.message || "Kunde inte spara redovisningsstandard"
          );
        }
      }
    } catch (err) {
      console.error("Failed to save accounting standard:", err);
      toast.error("Kunde inte spara redovisningsstandard");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      onClose &&
      e.target === backdropRef.current &&
      mouseDownTargetRef.current === backdropRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="fixed inset-0 bg-black/50 transition-opacity"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Välj redovisningsstandard
              </h2>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Recommendation banner */}
            {analysis.recommended === "K3" && (
              <div
                className={`mb-4 p-3 rounded-lg ${
                  analysis.mustUseK3
                    ? "bg-amber-50 border border-amber-200"
                    : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div className="flex items-start gap-2">
                  {analysis.mustUseK3 ? (
                    <AlertTriangle
                      size={18}
                      className="text-amber-600 mt-0.5 flex-shrink-0"
                    />
                  ) : (
                    <Info
                      size={18}
                      className="text-blue-600 mt-0.5 flex-shrink-0"
                    />
                  )}
                  <div>
                    <span
                      className={`text-sm font-medium ${
                        analysis.mustUseK3 ? "text-amber-800" : "text-blue-800"
                      }`}
                    >
                      {analysis.mustUseK3
                        ? "K3 är obligatoriskt"
                        : "K3 rekommenderas"}
                    </span>
                    {analysis.reason && (
                      <p
                        className={`text-xs mt-1 ${
                          analysis.mustUseK3
                            ? "text-amber-600"
                            : "text-blue-600"
                        }`}
                      >
                        {analysis.reason}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Locked notice */}
            {isLocked && (
              <div className="mb-4 p-3 rounded-lg bg-gray-100 border border-gray-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} className="text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Redovisningsstandard kan inte ändras efter steg 2.
                  </span>
                </div>
              </div>
            )}

            {/* Standard selection cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* K2 Card */}
              <button
                onClick={() =>
                  !isLocked && !analysis.mustUseK3 && setSelectedStandard("K2")
                }
                disabled={isLocked || analysis.mustUseK3}
                className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                  selectedStandard === "K2"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                } ${
                  isLocked || analysis.mustUseK3
                    ? "opacity-60 cursor-not-allowed"
                    : ""
                }`}
              >
                {selectedStandard === "K2" && (
                  <div className="absolute top-3 right-3">
                    <Check size={20} className="text-primary-600" />
                  </div>
                )}
                <h3 className="font-semibold text-gray-900 mb-1">K2</h3>
                <p className="text-xs text-gray-500 mb-3">BFNAR 2016:10</p>
                <p className="text-sm text-gray-600 mb-3">
                  Förenklat regelverk för mindre företag. Passar de flesta
                  småföretag.
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center gap-1">
                    <Check size={12} className="text-green-500" />
                    Enklare noter
                  </li>
                  <li className="flex items-center gap-1">
                    <Check size={12} className="text-green-500" />
                    Ingen kassaflödesanalys
                  </li>
                  <li className="flex items-center gap-1">
                    <Check size={12} className="text-green-500" />
                    Ingen uppskjuten skatt
                  </li>
                </ul>
              </button>

              {/* K3 Card */}
              <button
                onClick={() => !isLocked && setSelectedStandard("K3")}
                disabled={isLocked}
                className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                  selectedStandard === "K3"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {selectedStandard === "K3" && (
                  <div className="absolute top-3 right-3">
                    <Check size={20} className="text-primary-600" />
                  </div>
                )}
                {analysis.recommended === "K3" && (
                  <span className="absolute top-3 right-3 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {analysis.mustUseK3 ? "Obligatorisk" : "Rekommenderad"}
                  </span>
                )}
                <h3 className="font-semibold text-gray-900 mb-1">K3</h3>
                <p className="text-xs text-gray-500 mb-3">BFNAR 2012:1</p>
                <p className="text-sm text-gray-600 mb-3">
                  Huvudregelverk med fullständig rapportering. Krävs för större
                  företag och moderbolag.
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center gap-1">
                    <Building2 size={12} className="text-blue-500" />
                    Kassaflödesanalys (obligatorisk)
                  </li>
                  <li className="flex items-center gap-1">
                    <Building2 size={12} className="text-blue-500" />
                    Uppskjuten skatt
                  </li>
                  <li className="flex items-center gap-1">
                    <Building2 size={12} className="text-blue-500" />
                    Utökade noter med jämförelsetal
                  </li>
                </ul>
              </button>
            </div>

            {/* Expandable threshold details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              {showDetails ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
              {showDetails
                ? "Dölj gränsvärdesanalys"
                : "Visa gränsvärdesanalys"}
            </button>

            {showDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-500 mb-3">
                  Företag som överskrider mer än ett av följande gränsvärden för
                  vart och ett av de två senaste räkenskapsåren klassificeras
                  som större företag och måste använda K3 (BFNAR 2006:11).
                </p>
                <div className="space-y-2">
                  {analysis.checks.map((check) => (
                    <div
                      key={check.id}
                      className={`flex items-center justify-between p-2 rounded ${
                        check.exceeded
                          ? "bg-amber-50 border border-amber-200"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            check.exceeded ? "text-amber-600" : "text-gray-400"
                          }
                        >
                          {check.icon}
                        </span>
                        <span className="text-sm text-gray-700">
                          {check.label}
                        </span>
                      </div>
                      <div className="text-right">
                        <span
                          className={`text-sm font-medium ${
                            check.exceeded ? "text-amber-700" : "text-gray-600"
                          }`}
                        >
                          {check.currentValue !== null
                            ? check.unit === "kr"
                              ? formatNumber(check.currentValue) + " kr"
                              : check.currentValue
                            : "Ej angivet"}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          / {check.unit === "kr"
                            ? formatNumber(check.threshold) + " kr"
                            : check.threshold + " " + check.unit}
                        </span>
                        {check.exceeded && (
                          <span className="ml-2 text-xs text-amber-600">
                            Överskridet
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {analysis.isParentCompany && (
                  <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-amber-600" />
                      <span className="text-sm text-amber-700">
                        Moderbolag som upprättar koncernredovisning
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* K3 features info */}
            {selectedStandard === "K3" && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  K3 kräver följande tillägg:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Kassaflödesanalys (visas som nytt steg i wizarden)</li>
                  <li>• Uppskjuten skatt-beräkning</li>
                  <li>• Jämförelsetal i alla noter</li>
                  <li>
                    • Ackumulerade avskrivningar i anläggningstillgångsnot
                  </li>
                  {analysis.isParentCompany && (
                    <li>• Koncernredovisning (konsoliderad rapport)</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            {onClose && (
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Avbryt
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isSaving || isLocked}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sparar...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Välj {selectedStandard}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
